import ConvertIds from '@atlas/atlas-convert-ids';
import propCache from '@atlas/prop-cache';
import markerUtils from './markerUtils';

/** @typedef {{x:number, y:number}} Point2d */
/** @typedef {{x:number, y:number, z:number}} Point3d */
/**
 * @typedef {object} LocData
 * @property {Point3d} location
 * @property {import('./markerUtils').SVGElementExt} [marker]
 * @property {any[]} parentId
 * @property {string} parentPsId
 * @property {string} type
 * @property {number} xPx
 * @property {number} yPx
 * @property {boolean} isPrototypeSpace
 * @property {object} [sgo]
 */
/** @typedef {import('./core-elements/BasicElements').SelectionObject} SelectionObject */
/**
 * This class represents a "root part context" which is the triple of root part + work part + work occ.
 *
 * @typedef {{ displayPartAtlasId: Array, workPartAtlasId: Array, workComponentAtlasId: Array }} PartContext
 */
/**
 * @callback GetPartContextCache
 * @returns {PartContext}
 */
/**
 * @callback GetChildComponents
 * @param {string} id - JSON.stringify atlasId
 * @returns {Set<Array>}
 */
/**
 * @callback GetParentComponents
 * @param {object} viewer - An instance of the PLMVisWeb viewer object
 * @param {string} id - JSON.stringify atlasId
 * @returns {Set<string>} atlasId in string
 */
// NSAF-1576 NX Colors
/**
 * @enum {number}
 */
export const SELECTION_COLORS = {
    PRESELECTION: 0xf5bd93, // Light Carrot
    SELECTION: 0xdc6914, // Dark Carrot
    SECONDARY: 0x64c054 // Secondary Selection Color
};

/**
 * @enum {number}
 */
export const SELECTION_REASONS = {
    FOCUS_IN: 1,
    FOCUS_OUT: 2,
    TERMINATE: 3
};

/**
 * @enum {number}
 */
export const DISPLAY_DELAY = {
    MOUSE: 40,
    AREA: 100
};

/**
 * Cache mouse hover display delay value
 *
 * @type {number}
 */
let _mouseHoverDisplayDelay = DISPLAY_DELAY.MOUSE;

/**
 * Sets the mouse hover display delay value
 *
 * @param {number} value - mouse hover display delay value
 */
export function setMouseHoverDisplayDelay(value) {
    if (value) {
        _mouseHoverDisplayDelay = value < 0 ? 0 : value;
    }
}

/**
 * Gets the mouse hover display delay value
 * The following is the precedence of the value:
 * a) the default DISPLAY_DELAY.MOUSE
 * b) the display delay value set by setMouseHoverDisplayDelay
 * c) the display delay value set in localStorage by "mouseHoverDisplayDelay"
 * where c) has the highest precedence and a) has the lowest precedence
 *
 * @returns {number} - the value of the mouse hover display delay
 */
export function getMouseHoverDisplayDelay() {
    let displayDelay = _mouseHoverDisplayDelay.toString();
    const delayValue = localStorage?.getItem('mouseHoverDisplayDelay');
    if (delayValue && typeof delayValue === 'string') {
        let matches = delayValue.match(/(\d+)/); // this ignores the sign in front of the number
        displayDelay = matches && matches.length > 0 ? matches[0] : DISPLAY_DELAY.MOUSE.toString();
    }
    return parseInt(displayDelay);
}

/**
 * Defines the selection scope
 *
 * @enum {number}
 */
export const SELECTION_SCOPE = {
    EntireAssembly: 1,
    WithinWorkPartAndComponents: 2,
    WithinWorkPartOnly: 3
};

let _selectionScope = undefined;
/**
 * Sets the default selection scope
 *
 * @param {SELECTION_SCOPE} selectionScope - one of SELECTION_SCOPE value and only the valid selection scope gets set.
 */
export function setDefaultSelectionScope(selectionScope) {
    const validScope =
        selectionScope === SELECTION_SCOPE.EntireAssembly ||
        selectionScope === SELECTION_SCOPE.WithinWorkPartAndComponents ||
        selectionScope === SELECTION_SCOPE.WithinWorkPartOnly;
    if (validScope) {
        _selectionScope = selectionScope;
    }
}

/**
 * Gets the default selection scope
 *
 * @returns {SELECTION_SCOPE} - the default selection scope
 */
export function getDefaultSelectionScope() {
    return _selectionScope;
}

/**
 * Bit mask snap options. The snap option can be used individually or together.
 *
 * @enum {number}
 */
export const SnapOptions = {
    End: 1 << 0,
    Mid: 1 << 1,
    Center: 1 << 2,
    Poles: 1 << 3,
    DefiningPoints: 1 << 4,
    AllSnaps: 0xf
};
SnapOptions.AllSnaps = SnapOptions.End | SnapOptions.Mid | SnapOptions.Center | SnapOptions.Poles | SnapOptions.DefiningPoints;

const isValidSnapOption = (snapOption) => {
    return snapOption >= 0 && snapOption <= SnapOptions.AllSnaps; // 0 to disable the snap points.
};

let _snapOption = SnapOptions.End | SnapOptions.Mid | SnapOptions.Center;
/**
 * Sets the valid snap option as default snap option. The valid snap options are defined in the exported enum SnapOptions
 *
 * @param {number} snapOption - the snap option
 */
export function setDefaultSnapOption(snapOption) {
    if (isValidSnapOption(snapOption)) {
        _snapOption = snapOption;
    }
}

/**
 * Gets the snap Option
 */
export function getDefaultSnapOption() {
    return _snapOption;
}

/**
 * PLMVisWeb has special way to name the ids for its dimension as "DIM:xxxx" where xxxx is some integer number.
 * viewer.getObjectTypeByPsId doesn't work on the dimension psId. isDimension handles this special case.
 *
 * @param {string} psId - PLMVisWeb psId
 * @returns {boolean} - true if psId is PLMVisWeb Dimension psId; false otherwise
 */
export function isDimension(psId) {
    let delims = ':';
    let tokens = psId.split(delims);
    if (tokens && tokens.length > 0 && tokens[0] === 'DIM') {
        return true;
    }
    return false;
}

/**
 * Returns the object type of the input psId
 *
 * @param viewer
 * @param {string} psId - PLMVisWeb psId
 * @returns {string} object type
 */
export function getObjectTypeByPsId(viewer, psId) {
    // handle dimensions
    if (isDimension(psId)) {
        return 'Dimension';
    }

    let type = viewer.getObjectTypeByPsId(psId);
    if (!type || type.length === 0) {
        //TODO every breath I take is pure agony
        var pmiMgr = viewer.pmi;
        if (pmiMgr) {
            type = pmiMgr.isPmiObject(psId) ? 'PMI' : '';
        }
    }

    return type;
}

/**
 * Returns the distance between two 2D locations
 *
 * @param {Point2d} loc1 - 2D location object in the form of { x: xValue, y: yValue }
 * @param {Point2d} loc2 - 2D location object in the form of { x: xValue, y: yValue }
 * @returns {number} distance between 2D locations
 */
export function dist2(loc1, loc2) {
    return Math.sqrt(Math.pow(loc1.x - loc2.x, 2) + Math.pow(loc1.y - loc2.y, 2));
}

/**
 * Returns the distance between two 3D locations
 *
 * @param {Point3d} loc1 - 3D location object in the form of { x: xValue, y: yValue, z: zValue }
 * @param {Point3d} loc2 - 3D location object in the form of { x: xValue, y: yValue, z: zValue }
 * @returns {number} distance between 3D locations
 */
export function dist3(loc1, loc2) {
    return Math.sqrt(Math.pow(loc1.x - loc2.x, 2) + Math.pow(loc1.y - loc2.y, 2) + Math.pow(loc1.z - loc2.z, 2));
}

/**
 * Returns true if the distance between the two 2D locations is within the input tolerance
 *
 * @param {Point2d} loc1 - 2D location object in the form of { x: xValue, y: yValue }
 * @param {Point2d} loc2 - 2D location object in the form of { x: xValue, y: yValue }
 * @param {number} tol - tolerance for checking if the 2D locations are the same
 * @returns {boolean} - true if two 2D locations are withing the tolerance; false otherwise
 */
export function isSame2DLocation(loc1, loc2, tol) {
    if (dist2(loc1, loc2) < tol) {
        return true;
    }
    return false;
}

/**
 * Returns true if the distance between the two 3D locations is within the input tolerance
 *
 * @param {Point3d} loc1 - 3D location object in the form of { x: xValue, y: yValue, z: zValue }
 * @param {Point3d} loc2 - 3D location object in the form of { x: xValue, y: yValue, z: zValue }
 * @param {number} tol - tolerance for checking if the 3D locations are the same
 * @returns {boolean} - true if two 3D locations are withing the tolerance; false otherwise
 */
export function isSame3DLocation(loc1, loc2, tol) {
    if (dist3(loc1, loc2) < tol) {
        return true;
    }
    return false;
}

/**
 * Callback function for getting different point rules
 *
 * @param {Function} callback - the actual callback function for getting the point rule.
 * @param {object} locData - loc object defined the same as in generateSelObj
 * @returns {object} - the point rule
 */
function getPointRuleWrapper(callback, locData) {
    return callback(locData);
}

/**
 * Generates a selection object from the input
 *
 * @param {Function} getPointRule - the callback function for getting the point rule which takes the second parameter
 *                                in the parameter list as the input
 * @param {LocData} loc - loc object which contains the following properties:
 *                       {
 *                           location: { x: aaa, y: bbb, z: ccc },
 *                           marker: ellipse { ... },
 *                           parentId: ...,
 *                           parentPsId: ...,
 *                           type: 'SnapOnFace',
 *                           xPx: ...,
 *                           yPx: ...
 *                       }
 * @param {string} parentPsId - psId of the parent
 * @param {object} viewer - the viewer object
 * @param {string} pointType - snap point type. 'SnapPoint', 'SnapOnFace', 'SnapOnCurve', etc.
 * @param {number} counter - sequence number for the snap point
 * @param {import('./SnapPointBaseElement').SnapPointBaseElement} selEle - Selection element that creating this selObj
 * @param {typeof import('./SnapPointBaseElement').SnapPointSelectionObject} selObjCls selObj
 * @returns {import('./SnapPointBaseElement').SnapPointSelectionObject} - the point rule
 */
export function generateSelObj(getPointRule, loc, parentPsId, viewer, pointType, counter, selEle, selObjCls) {
    let assyAtlasId = ConvertIds.fromPsId(viewer, parentPsId);
    if (!ConvertIds.isSameContext(assyAtlasId, loc.parentId)) {
        if (ConvertIds.isSamePrototype(assyAtlasId, loc.parentId)) {
            loc.parentId = ConvertIds.toFullAtlasId(loc.parentId, assyAtlasId);
        }
    }

    let rule = getPointRuleWrapper(getPointRule, loc);
    // Since id should be unique, a curve can have many point on curve, I append the counter in the id.
    // This can be changed if better idea arise.
    let pointId =
        counter === undefined ? JSON.stringify(loc.parentPsId + '_' + loc.type) : JSON.stringify(loc.parentPsId + '_' + loc.type + '_' + counter);
    let selObj = new selObjCls(pointId, [parentPsId], pointType, selEle?.getDisplayName(), selEle);
    selObj.recipe = rule;
    selObj.result = loc;
    selObj.cancelAccepted = true;
    selObj.marker = loc.marker;

    return selObj;
}

/**
 * pointObj currently contains the following properties:
 * {
 *     location: { x: aaa, y: bbb, z: ccc },
 *     marker: ellipse { ... },
 *     parentId: ...,
 *     type: 'SnapOnCurve',
 *     xPx: ...,
 *     yPx: ...
 * }
 * pointObjsEqual checks if the two input pointObjs:
 * a) have the same 3D locations;
 * b) have the same 2D locations;
 * c) have the same parent;
 * d) have the same type
 *
 * @param {LocData} pointObj1
 * @param {LocData} pointObj2
 */
const pointObjsEqual = (pointObj1, pointObj2) => {
    let validObjs = pointObj1 && pointObj2;
    if (!validObjs) {
        return false;
    }
    let tol = 1.0e-10;
    if (!isSame3DLocation(pointObj1.location, pointObj2.location, tol)) {
        return false;
    }
    if (!isSame2DLocation({ x: pointObj1.xPx, y: pointObj1.yPx }, { x: pointObj2.xPx, y: pointObj2.yPx }, tol)) {
        return false;
    }
    if (pointObj1.parentPsId !== pointObj2.parentPsId) {
        return false;
    }
    if (pointObj1.type !== pointObj2.type) {
        return false;
    }
    return true;
};

/**
 * Callback wrapper function for calling displayPoint
 *
 * @param {Function} callback - callback function provided by the caller
 * @param {string} psId - psId of the point parent
 * @param {object} pointData - point data object
 * @param {object} mouseLoc - mouse location
 */
async function displayPointWrapper(callback, psId, pointData, mouseLoc) {
    await callback(psId, pointData, mouseLoc);
}

/**
 * This is the mouseMove callback function for SnapOnCurveElement and SnapOnFaceElement only. This function is no op for
 * any other callers
 *
 * @param {object} mouseMoveParams - parameters for mouseMove.
 * @param {object} sgoUtils - an object that provides api for sgo, i.e. mouse over, hightlight
 * mouseMoveParasms contains the following properties:
 *   {
 *     eventData: eventData,       // <I> mouse move event data
 *     curObjects: curObjects,     // <I> parent object currently under the mouse
 *     viewer: viewer,             // <I> an instance of PLMVisWeb viewer object
 *     sgoMgr: sgoMgr,             // <I> an instance of SGO manager
 *     svgOverlay: svgOverlay,     // <I> svgOverlay object for markers
 *     snapType: snapType,         // <I> 'SnapOnCurve' or 'SnapOnFace'
 *     displayPoint: displayPoint, // <I> function to display the point provided by either SnapOnCurveElement or SnapOnFaceElement
 *     markRadius: markRadius,     // <I> radius of the marker
 *     preselId: preselId,         // <IO> preselected parent psId
 *     highlighLoc: highlightLoc   // <IO> point data object currently in highlight
 *   }
 */
export async function mouseMoveCallback(mouseMoveParams, sgoUtils) {
    // This function is only for 'SnapOnCurve' or 'SnapOnFace' snapType
    if (!(mouseMoveParams.snapType === 'SnapOnCurve' || mouseMoveParams.snapType === 'SnapOnFace')) {
        return;
    }

    let _objType = undefined;
    let propName = undefined;
    if (mouseMoveParams.snapType === 'SnapOnCurve') {
        _objType = 'DisplayEntity';
        propName = 'curveId';
    } else {
        _objType = 'face';
        propName = 'faceId';
    }

    /*   if (camChange){
           markerUtils.updateMarkers( PLMVisWeb, mouseMoveParams.viewer, mouseMoveParams.svgOverlay, mouseMoveParams.markRadius );
           return;
       } */
    let mouseLoc = { x: mouseMoveParams.eventData.offsetX, y: mouseMoveParams.eventData.offsetY };
    let modelPoint = undefined;
    let result = null;
    if (mouseMoveParams.curObjects.length > 0) {
        // eventually we need to handle more complicated case, where
        // point candidate fails validation and another underneath passes
        for (let obj of mouseMoveParams.curObjects) {
            if (obj.isBlocked) {
                break;
            }
            if (obj.type === _objType) {
                result = obj;
                break;
            }
        }
    }
    let pointData = undefined;
    if (!result) {
        if (mouseMoveParams.preselId) {
            // markerUtils.clearMarkers( mouseMoveParams.svgOverlay );
            // PR10657106: unable to locate the "center" keypoints on an arc. This is because clearMarkers erases ALL markers including center when
            // both SnapOnCurve and SnapPoint are turned on.
            markerUtils.clearMarkersOfLocType(mouseMoveParams.svgOverlay, mouseMoveParams.snapType);
            sgoUtils.highlightSGOs(mouseMoveParams.sgoMgr, false);
        }
        mouseMoveParams.preselId = null;
    } else {
        let psId = result.id;
        let parentId = ConvertIds.fromPsId(mouseMoveParams.viewer, psId);
        let modelPoint = result.modelPoint;
        pointData = {
            point: {
                type: mouseMoveParams.snapType,
                location: { x: modelPoint[0], y: modelPoint[1], z: modelPoint[2] },
                parentId: parentId,
                parentPsId: psId,
                xPx: mouseLoc.x,
                yPx: mouseLoc.y,
                isPrototypeSpace: false
            }
        };
        await displayPointWrapper(mouseMoveParams.displayPoint, psId, pointData, mouseLoc);
        // check if a selected point is at the mouse location. Use the selected point if it exists
        let locTol = mouseMoveParams.markRadius * 1.5;
        let sgo = sgoUtils.getSGOAtMouseLoc(mouseMoveParams.viewer, mouseMoveParams.sgoMgr, mouseLoc, locTol, true);
        if (sgo) {
            markerUtils.clearMarkers(mouseMoveParams.svgOverlay); // get rid of the current marker
            pointData.point.sgo = sgo;
            pointData.point.location = sgo.SPLM_Loc.location;
            pointData.point.xPx = sgo.SPLM_Loc.xPx;
            pointData.point.yPx = sgo.SPLM_Loc.yPx;
            sgoUtils.onMouseOverSGO(sgo, mouseMoveParams.eventData);
        } else {
            // mouse over point on curve if mouseMoveParams.snapType === 'SnapOnCurve'; face if mouseMoveParams.snapType === 'SnapOnFace'
            markerUtils.onMouseOver(pointData.point.marker, mouseMoveParams.eventData);
        }
        if (psId !== mouseMoveParams.preselId) {
            mouseMoveParams.preselId = psId;
        }
    }
    // mouseMoveParams.preselId - the parent curve (mouseMoveParams.snapType === 'SnapOnCruve') or face (mouseMoveParams.snapType === 'SnapOnFace') on which we're getting the point
    let newLoc = null;
    let retObj = undefined;
    if (pointData) {
        // we got the point on curve (mouseMoveParams.snapType === 'SnapOnCurve' or face (mouseMoveParams.snapType === 'SnapOnFace')
        newLoc = pointData.point;
        // markerUtils.onMouseOver( newLoc.marker, mouseMoveParams.eventData );
        if (!result) {
            retObj = { id: result.id, isBlocked: false, type: _objType };
            retObj[propName] = result.id;
        }
    }
    if (!pointObjsEqual(mouseMoveParams.highlightLoc, newLoc)) {
        if (mouseMoveParams.highlightLoc && mouseMoveParams.highlightLoc.marker) {
            markerUtils.onMouseOut(mouseMoveParams.highlightLoc.marker, mouseMoveParams.eventData);
        }
        mouseMoveParams.highlightLoc = newLoc;
    }

    if (mouseMoveParams.highlightLoc === null) {
        if (mouseMoveParams.curObjects && mouseMoveParams.curObjects.length > 0 && !result) {
            // This is the case where no face or curve is under the cursor ( face for SnapOnFaceElement or curve for SnapOnCurveElement),
            // and the cursor could be over some other type of snap point
            // We don't want to reset "cursor over" over the other type of snap point.
            return;
        }
        markerUtils.onMouseOut(null, mouseMoveParams.eventData); // cursor is away from Loc marker. change cursor style back
    }

    return retObj;
}

// otherPsIds contains additionalSort.edgePsIds + additionalSort.otherPsIds
/**
 * Sorts inputPsIds and put:
 *     a) pmiPsIds into pmiPsIds
 *     b) dimensionPsIds into additionalSort.dimensionPsIds
 *     c) the psIds other pmi and dimension psIds into otherPsIds
 *          furthermore, it sorts the psIds in otherPsIds and put
 *          i) edgePsIds into  additionalSort.edgePsIds
 *         ii) psIds other than edges in otherPsIds in additionalSort.otherPsIds
 *
 * @param {object} viewer - the instance of the viewer
 * @param {Array<string>} inputPsIds - psIds of objects
 * @param {Array<string>} otherPsIds - the psIds in inputPsIds which are not PMI and Dimension
 * @param {Array<string>} pmiPsIds - pmi psIds in inputPsIds
 * @param {object} [additionalSort] - Object containing edgePsId array and the rest of psIds in otherPsIds
 */
export function sortPsIds(viewer, inputPsIds, otherPsIds, pmiPsIds, additionalSort = undefined) {
    for (let psId of inputPsIds) {
        let type = getObjectTypeByPsId(viewer, psId);
        if (type === 'PMI') {
            pmiPsIds.push(psId);
        } else if (type === 'Dimension') {
            // we don't want to put dimension in otherPsIds since PLMVisWeb cannot handle that in the normal
            // highlight/dehighlight and selection.
            if (additionalSort) {
                additionalSort.dimensionPsIds.push(psId);
            }
        } else {
            otherPsIds.push(psId);
            if (additionalSort) {
                if (type === 'Edge') {
                    additionalSort.edgePsIds.push(psId);
                } else if (additionalSort.regionPsIds && (type === 'RegionFaceType' || type === 'RegionSketchType')) {
                    additionalSort.regionPsIds.push(psId);
                } else {
                    additionalSort.otherPsIds.push(psId);
                }
            }
        }
    }
}

// PLMVisWeb edge highlight is very hard to see if the edges are behind other objects. They recommend to put the
// edge on top when highlight and remove the on top when done highlight. I really hope that PLMVisWeb can build this in.
//
// handleEdgeOnTopInContext: a) display edges in psIds onTop using the specified highlight color
//                           b) display the selected edges in context onTop using the specified selection color
//     highlight === true:
//         displays the edges in psIds in original onTop edge color
//     highlight === false:
//         removes the edges in psIds from onTop edge display.
// In both cases, displays the selected edges in context as onTop and use the selection color except in very
// rare case, edges in psIds may also exist in the selected edges in the context. For example, when we deselect
// an edge. In this case, we need to tell handleEdgeOnTopInContext what to do.
//     overrideHighlight === true:
//         displays the edges in psIds which are the same as the ones in selected edges in the selection color
//     overrideHighlight === false:
//         displays the edges in psIds which are the same as the ones selected edges in highlight color (this is the case for deselect an edge)
/**
 * Highlight the objects represented by psIds with edge on top if they are obscured by other objects
 *
 * @param {object} viewer - An instance of the plmvisweb viewer object
 * @param {object} context - The atlas-selection context
 * @param {object} plmvisweb - An instance of the plmvisweb
 * @param {string[]} psIds - Array of psIds
 * @param {boolean} highlight - true to highlight and false to dehighlight
 * @param {boolean} [overrideHighlight] - If the candidate to highlight is a selected object, true to override the highlight and display the selection color; false to display highlight color
 * @deprecated This function will be deprecated.
 */
export function handleEdgeOnTopInContext(viewer, context, plmvisweb, psIds, highlight, overrideHighlight = true) {
    let edges = [];
    for (const psId of psIds) {
        if (getObjectTypeByPsId(viewer, psId) === 'Edge') {
            edges.push(psId);
        }
    }
    if (highlight) {
        if (edges.length > 0) {
            if (viewer.addOnTopEdges) {
                viewer.addOnTopEdges(edges);
            } else {
                //PLMVW 1.7.68+
                viewer.clearOnTopEdges();
                // Use SWF-3D Preselection Pale Crimson (NSAF-1576).
                viewer.setOnTopEdgesByPsId(edges, true, { diffuse: SELECTION_COLORS.PRESELECTION });
            }
        }
    } else {
        if (viewer.removeOnTopEdges) {
            viewer.removeOnTopEdges();
        } else {
            //PLMVW 1.7.68+
            viewer.clearOnTopEdges();
        }
    }

    // Since NSAF-1404 asks for keep the selected edges onTop. We need to make sure the selected edges are
    // always displayed as onTop using the selection color
    let selected = Array.from(context.getSelected());
    if (selected && selected.length > 0) {
        let selEdgeObjs = selected.filter((selectedObj) => selectedObj.type === 'Edge');
        let selEdgePsIds = selEdgeObjs.map((obj) => obj.psIds[0]);
        // remove edges from selEdgePsIds if any if highlight === true
        // This means someone wants to highlight edges in different way for example deselect prehighlight.
        if (!overrideHighlight) {
            for (const edge of edges) {
                selEdgePsIds = selEdgePsIds.filter((edgePsId) => edgePsId !== edge);
            }
        }
        let materialPool = viewer.materialPool;
        let highlightMat = materialPool.getMaterialDefinition(plmvisweb.DefaultIndices.MeshSelectionMat);
        viewer.setOnTopEdgesByPsId(selEdgePsIds, true, highlightMat);
    }
}

/**
 * Sets the dimensions in dimPsIds as selected (selected === true) or deselected (selected === false)
 *
 * @param {object} viewer - an instance of the viewer
 * @param {Array<string>} dimPsIds - array of dimensoin psIds
 * @param {boolean} selected - true to select and false to deselect
 */
export function setDimensionSelection(viewer, dimPsIds, selected) {
    let dimManager = viewer.dimension;
    if (dimManager) {
        if (dimPsIds.length > 0) {
            for (let dimPsId of dimPsIds) {
                dimManager.setSelectionByDimId(dimPsId, selected);
            }
        }
    }
}
let _inheritOriginalCurveStyles = true;
/**
 * Gets whether to inherit the original curve styles
 *
 * @returns {boolean} true to inherit the original curve styles; false otherwise
 */
export function getInheritOriginalCurveStyles() {
    return _inheritOriginalCurveStyles;
}
/**
 * Sets whether to inherit the original curve styles
 *
 * @param {boolean} inheritStyles   true to inherit the original curve styles; false otherwise
 */
export function setInheritOriginalCurveStyles(inheritStyles) {
    _inheritOriginalCurveStyles = inheritStyles;
}

/**
 * Sets the curve styles for highlight with the same curve styles as the original curves
 *
 * @param {object} viewer - Instance of the plmvisweb viewer
 * @param {string[]} psIds - Array of plmvisweb psIds
 */
export function setCurveHighlightStyles(viewer, psIds) {
    if (getInheritOriginalCurveStyles()) {
        psIds.forEach((psId) => {
            if (isCurveFromPsId(viewer, psId)) {
                const mat = viewer.getMaterialByPsId(psId);
                viewer.setHighlightMaterialByPsId(psIds, {
                    dashed: mat[0].dashed,
                    strokeDashArray: mat[0].strokeDashArray,
                    stippleFactor: mat[0].stippleFactor,
                    stipplePattern: mat[0].stipplePattern
                });
            }
        });
    }
}

/**
 * Sets the curve styles for selection with the same curve styles as the original curves
 *
 * @param {object} viewer - Instance of the plmvisweb viewer
 * @param {string[]} psIds - Array of plmvisweb psIds
 */
export function setCurveSelectionStyles(viewer, psIds) {
    if (getInheritOriginalCurveStyles()) {
        psIds.forEach((psId) => {
            if (isCurveFromPsId(viewer, psId)) {
                const mat = viewer.getMaterialByPsId(psId);
                viewer.setSelectionMaterialByPsId(psIds, {
                    dashed: mat[0].dashed,
                    strokeDashArray: mat[0].strokeDashArray,
                    stippleFactor: mat[0].stippleFactor,
                    stipplePattern: mat[0].stipplePattern
                });
            }
        });
    }
}

/**
 *
 * @param {object} viewer - PlmVisWeb viewer instance
 * @param {import('./Context').Context} context
 * @param {object} PLMVisWeb
 * @param {Iterable<SelectionObject>} selObjects
 * @param {SELECTION_REASONS} reason
 * @param {boolean} [selected=true]
 * @returns {void}
 */
export function displaySelectedInContext(viewer, context, PLMVisWeb, selObjects, reason, selected = true) {
    if (!selObjects) {
        return;
    }
    let psIds = [];
    let pmiIds = [];
    let additionalSort = { edgePsIds: [], dimensionPsIds: [], otherPsIds: [] };
    for (let obj of selObjects) {
        let objPsIds = obj.psIds;
        if (obj.sourceElement) {
            objPsIds = obj.sourceElement.select(obj, selected, reason);
        }
        sortPsIds(viewer, objPsIds, psIds, pmiIds, additionalSort);
    }
    if (psIds.length > 0) {
        let actualPsIdSet = new Set();
        // When selected === true and psIds contains faces, we need to add the edges of the faces that are not in the context
        // to plmvisweb selected edges.
        // When selected === false and psIds contains faces, we need to remove the edges of the faces that are not in the context
        // from plmvisweb selected edges.
        //
        // In either cases, we need to include the edges of faces that are not in context to the original psIds to process
        psIds.forEach((psId) => {
            actualPsIdSet.add(psId);
            if (viewer.getObjectTypeByPsId(psId) === 'Face') {
                const edgePsIds = viewer.getEdgeIdsById(psId);
                const edgePsIdsNotInContext = edgePsIds.filter(
                    (psId) =>
                        // We need to see if the edge is actually selected
                        !context.isAtlasObjSelected(ConvertIds.fromPsId(viewer, psId))
                );
                edgePsIdsNotInContext.forEach(actualPsIdSet.add, actualPsIdSet);
            }
        });
        setCurveSelectionStyles(viewer, Array.from(actualPsIdSet));
        viewer.setSelectionByPsId(Array.from(actualPsIdSet), selected);
    }
    //TODO please let me escape my misery
    let pmiMgr = viewer.pmi;
    if (pmiMgr && pmiIds.length > 0) {
        pmiMgr.setSelectionByPsId(pmiIds, selected, true, true);
    }
    // handle dimension selection
    setDimensionSelection(viewer, additionalSort.dimensionPsIds, selected);
}

/**
 *  Create a color object for dimension display from the input material definition object
 *
 *  @param {object} matDef - PLMVisWeb material definition object
 *  @returns {object} - dimension color object
 */
export function createDimensionColorObject(matDef) {
    let color = matDef.diffuse;
    let colorObj = {
        text: color,
        extensionLine: color,
        extensionLineGap: color,
        dimensionLine: color
    };
    return colorObj;
}

/**
 *  Display psIds using the specified material defined in matDef
 *
 * @param viewer
 *  @param {Array<string>} allPsIds - psIds to be displayed using the specified material definition
 *  @param {object} matDef - plmvisweb material definition to display all the psIds. If null or undefined,
 *                          resetMaterialByPsId is called
 */
export function setMaterialByPsIds(viewer, allPsIds, matDef) {
    let psIds = [];
    let pmiPsIds = [];
    let additionalSort = { edgePsIds: [], dimensionPsIds: [], otherPsIds: [] };
    sortPsIds(viewer, allPsIds, psIds, pmiPsIds, additionalSort);
    if (matDef) {
        viewer.setMaterialByPsId(psIds, matDef);
        let colorObject = createDimensionColorObject(matDef);
        for (const dimId of additionalSort.dimensionPsIds) {
            viewer.dimension.setDimensionColors(dimId, colorObject);
        }
        viewer.pmi.setMaterialByPsId(pmiPsIds, matDef);
    } else {
        for (const psId of psIds) {
            viewer.resetMaterialByPsId(psId);
        }
        for (const dimId of additionalSort.dimensionPsIds) {
            viewer.dimension.resetDimensionColors(dimId);
        }
        // Currently, there is no reset PMI material. Request has been sent to PLMVisWeb
        // for ( const pmiPsId of pmiPsIds ) {
        //     viewer.pmi.resetMaterialByPsId ( pmiPsId );
        // }
    }
}

/**
 *  Given an array of the inactive selection contexts and the current selection context, this function gets the selected objects and the
 *  objects in their psIds from the inactive select contexts excluding the selected objects and objects in their psId arrays in the
 *  current selection context.
 *
 * @param {import('./Context').Context} curSelContext
 * @param {import('./Context').Context[]} inactiveSelContexts
 * @returns {{ selectedObjects: Set<any>, selectedObjectPsIds: Set<any>}}
 */
export function getSelectedObjectsInactiveContexts(curSelContext, inactiveSelContexts) {
    let curContextSelObjs = Array.from(curSelContext.getSelected());
    let curContextSelObjPsIdSet = curSelContext.getPsIdsInSelected(); // make sure removing Array.from in Context.js

    // get the psIds of the objects in the inactive contexts that are not in "curContextSelObjPsIdSet"
    let selObjs = { selectedObjects: new Set(), selectedObjectPsIds: new Set() };
    if (inactiveSelContexts.length > 0) {
        for (const context of inactiveSelContexts) {
            let contextSelObjs = Array.from(context.getSelected());
            for (const selObj of contextSelObjs) {
                if (!curContextSelObjs.includes(selObj)) {
                    selObjs.selectedObjects.add(selObj);
                }
            }
            let selObjPsIds = Array.from(context.getPsIdsInSelected());
            for (const selObjPsId of selObjPsIds) {
                if (!curContextSelObjPsIdSet.has(selObjPsId)) {
                    selObjs.selectedObjectPsIds.add(selObjPsId);
                }
            }
        }
    }
    return selObjs;
}

/**
 * Set the selected objects in the selection context in the primary selection color.
 *
 * @param {object} viewer - an instance of PLMVisWeb viewer manager
 * @param {object} plmvisweb - plmvisweb object
 * @param {import('./Context').Context} context - the selection context
 */
export function setPrimarySelectionColor(viewer, plmvisweb, context) {
    // Before PLMVisWeb has a better strategy in supporting the secondary selection color (material), we need
    // to add the selected objects in the context back in the viewer.
    let selectedObjs = Array.from(context.getSelected());
    if (selectedObjs.length > 0) {
        displaySelectedInContext(viewer, context, plmvisweb, selectedObjs, SELECTION_REASONS.FOCUS_IN, true);
    }
}

let pointCache = {}; // cache points on curve
let apiId = 0;

const curveTypes = new Set(['SPLM.Line', 'SPLM.Arc', 'SPLM.Ellipse', 'SPLM.Spline', 'SPLM.Parabola', 'SPLM.Hyperbola', 'SPLM.Polyline']);
const isCurve = (objClass) => {
    return curveTypes.has(objClass);
};

const plmVWCurveTypes = new Set(['Line', 'Arc', 'Ellipse', 'Spline', 'Parabola', 'Hyperbola', 'Polyline']);
export const isCurveFromPsId = (viewer, psId) => {
    let type = getObjectTypeByPsId(viewer, psId);
    let referencePrefix = 'Reference ';
    if (type.startsWith(referencePrefix)) {
        type = type.substring(referencePrefix.length);
    }
    return plmVWCurveTypes.has(type);
};

export const isPointFromPsId = (viewer, psId) => {
    let type = getObjectTypeByPsId(viewer, psId);
    return type === 'Point';
};

/**
 * @returns {object} - cached point data on curves
 */
export function getPointCache() {
    return pointCache;
}

/**
 * Given an array of input stringified atlas ids, return point data for those atlas ids.
 *
 * @param {Array<string>} inputAtlasIds - atlasIds of objects
 * @param {number} snapOption - snap point option defined in SnapOptions
 * @returns {any[][]}
 */
export function getCachedPoints(inputAtlasIds, snapOption) {
    let cache = [];
    for (const id of inputAtlasIds) {
        let atlasId = JSON.parse(id); // input used JSON.stringify on the atlasId
        let objectPoints = [];
        let cachedIds = propCache.getCachedByIds([atlasId]); // this could contain both solid edges and curves
        if ((cachedIds[0] && cachedIds[0].SolidEdgeType) || isCurve(cachedIds[0].Class)) {
            if (cachedIds[0].GetLocations) {
                for (let location of cachedIds[0].GetLocations.locations) {
                    location.parentId = atlasId;
                    location.floating = false;
                    let addIt = false;
                    if (snapOption === SnapOptions.AllSnaps) {
                        addIt = true;
                        if (snapOption & SnapOptions.Center && location.type === 'Center') {
                            location.floating = true;
                        }
                    } else {
                        if (snapOption & SnapOptions.End && (location.type === 'Start' || location.type === 'End')) {
                            addIt = true;
                        } else if (snapOption & SnapOptions.Mid && location.type === 'Mid') {
                            addIt = true;
                        } else if (snapOption & SnapOptions.Center && location.type === 'Center') {
                            location.floating = true;
                            addIt = true;
                        }
                    }
                    if (addIt) {
                        objectPoints.push(location);
                    }
                }
            }
            if (cachedIds[0].Class === 'SPLM.Spline') {
                let addPoles = Boolean(snapOption & SnapOptions.Poles);
                let addDefiningPoints = Boolean(snapOption & SnapOptions.DefiningPoints);
                if (cachedIds[0].GetPoles && addPoles) {
                    if (!cachedIds[0].GetPoles.locations) {
                        cachedIds[0].GetPoles.locations = [];
                        let idx = 0;
                        for (let pole of cachedIds[0].GetPoles.poles) {
                            let location = {
                                floating: true,
                                parentId: atlasId,
                                type: 'Pole',
                                location: { x: pole.x, y: pole.y, z: pole.z, w: pole.w },
                                poleSeqId: idx++
                            };
                            cachedIds[0].GetPoles.locations.push(location);
                        }
                    }
                    objectPoints.push(...cachedIds[0].GetPoles.locations);
                }
                if (cachedIds[0].GetDefiningPoints && addDefiningPoints) {
                    if (!cachedIds[0].GetDefiningPoints.locations) {
                        cachedIds[0].GetDefiningPoints.locations = [];
                        let idx = 0;
                        for (let defPoint of cachedIds[0].GetDefiningPoints.points) {
                            let location = {
                                parentId: atlasId,
                                type: 'DefiningPoint',
                                location: { x: defPoint.x, y: defPoint.y, z: defPoint.z },
                                defPointSeqId: idx++
                            };
                            cachedIds[0].GetDefiningPoints.locations.push(location);
                        }
                    }
                    objectPoints.push(...cachedIds[0].GetDefiningPoints.locations);
                }
            }
        }
        if (objectPoints && objectPoints.length > 0) {
            cache.push(objectPoints);
        }
    }
    return cache;
}

/**
 * resets the pointCache
 */
export function resetPointCache() {
    pointCache = {};
}

/**
 * set the current work part
 *
 * @param curContext
 * @param workPart
 * @param SPLM
 */
export const onWorkPartChanged = (curContext, workPart, SPLM) => {
    // if the work part has changed and a snap point element is active,
    // update the register right now.
    if (workPart && curContext.hasActiveElement('SnapPoint')) {
        registerPointCache(SPLM);
    } else {
        setPointCacheRegistered(false);
    }
};

/**
 * fuction to set register point cache
 */
let _pointCachedRegistered = false;
/**
 *
 * @param registered
 */
export function setPointCacheRegistered(registered) {
    _pointCachedRegistered = registered;
}

/**
 * fuction to get register point cache
 *
 * @returns {boolean}
 */
export function getPointCacheRegistered() {
    return _pointCachedRegistered;
}

export const registerPointCache = (SPLM) => {
    propCache.register('SPLM.Face', ['SolidFaceType', 'GetUnsortedEdges']);
    propCache.register('SPLM.Edge', ['SolidEdgeType', 'GetLocations']);
    setPointCacheRegistered(true);
    // get wf points if we can
    if (!SPLM) {
        return;
    }
    let workPart = undefined;
    const partContext = getPartContextCache();
    if (partContext) {
        workPart = partContext.workPartAtlasId;
    }
    if (!workPart) {
        setPointCacheRegistered(false);
        return;
    }
    let splmApiCached = SPLM.api;
    SPLM.api = [];

    let session = new SPLM.AtlasSession();
    let curveCollection = new SPLM.CurveCollection();
    let partAtlasId = workPart;
    let updatedPart = new SPLM.Part(partAtlasId);
    curveCollection.ToArray(updatedPart);

    propCache.register('SPLM.Curve', ['GetLocations']);
    propCache.register('SPLM.Spline', ['GetPoles', 'GetDefiningPoints']);
    let api = SPLM.api;
    propCache.cacheFromApi(api);
    SPLM.api = splmApiCached;
};

/**
 * function to set allow selection of hidden wireframe
 *
 * @type {boolean}
 */
let _allowSelectionOfHiddenWireframe = false;
/**
 *
 * @param value
 */
export function setAllowSelectionOfHiddenWireframe(value) {
    _allowSelectionOfHiddenWireframe = value;
}

/**
 * @returns {boolean}
 */
export function getAllowSelectionOfHiddenWireframe() {
    return _allowSelectionOfHiddenWireframe;
}

/**
 * @type {GetPartContextCache}
 */
let _getPartContextCache_fn = undefined;
/**
 * Registers the get cached part context function
 *
 * @param { GetPartContextCache } fn - get part context function
 */
export function registerGetPartContextCache_fn(fn) {
    _getPartContextCache_fn = fn;
}

/** @type {GetChildComponents} */
let _getChildComponents_fn = undefined;
/**
 * This function registers getChildComponents function which takes the atlasId of an assembly component and gets
 * a Set of the atlasIds of all its child components.
 *
 * @param {GetChildComponents} fn - the function of "getChildComponents" to be registered
 */
export function registerGetChildComponents_fn(fn) {
    _getChildComponents_fn = fn;
}

/** @type {GetParentComponents} */
let _getParentComponents_fn = undefined;
/**
 * This function registers getParentComponents function which takes the viewer and atlasId of an assembly component and gets
 * a Set of the atlasIds of all its parent components under the work part.
 *
 * @param {GetParentComponents} fn - the function of "getParentComponents" to be registered
 */
export function registerGetParentComponents_fn(fn) {
    _getParentComponents_fn = fn;
}

/**
 * Given an AtlasId of an assembly component, get all AtlasIds of its child components in a Set
 *
 * @param {Array} compAtlasId - AtlasId of assembly component
 * @returns {Set<Array>?} - A Set of atlasIds of the child assembly components
 */
export const getChildComponentSet = (compAtlasId) => {
    if (_getChildComponents_fn && compAtlasId) {
        const childComps = _getChildComponents_fn(JSON.stringify(compAtlasId));
        if (childComps.size > 0) {
            return childComps;
        }
        return null;
    }
    return null;
};

/**
 * Gets all the parent components up to the top assembly component for the current component
 *
 * @param {object} viewer - An instance of PLMVisWeb object
 * @param {string} compAtlasIdStr - stringified AtlasId of assembly component
 * @returns {Set<string>?} - A Set of atlasIds in strings of the parent assembly components
 */
export function getParentComponents(viewer, compAtlasIdStr) {
    if (_getParentComponents_fn && viewer && compAtlasIdStr) {
        const parentComps = _getParentComponents_fn(viewer, compAtlasIdStr);
        if (parentComps?.size > 0) {
            return parentComps;
        }
        return null;
    }
    return null;
}

/**
 * Gets the cached part context
 *
 * @returns {PartContext} - the part context containing displayPartAtlasId, workPartAtlasId, workComponentAtlasId,
 *                     and the atlas ids of the children parts of the workComponent.
 */
export function getPartContextCache() {
    return _getPartContextCache_fn ? _getPartContextCache_fn() : undefined;
}

/**
 * Some objects are without assembly context concept. For example, dimension objects. The
 * dimension objects are with ids of 'DIM:xxx' where xxx is some integer numbers. The
 * objects without assembly context should be ignored from checking selection scope.
 *
 * @param {SelectionObject} candidateObject - the candidate object
 * @returns {boolean} - true to ignore the object; false to keep the object
 */
function ignoreObjectFromSelectionScope(candidateObject) {
    // Dimensions always pass because we don't have any way to scope them, they're
    // not part of any assembly structure.  Parts always pass because if a selection
    // is scoped AND client has added PartElement, we assume they want to allow selection
    // of other components.  Otherwise the only part selectable is the work part.
    if (
        candidateObject.type === 'Dimension' ||
        (!getMouseOnCanvas() && candidateObject.type === 'Part') ||
        candidateObject.type === 'RegionSketchType' ||
        candidateObject.type === 'RegionFaceType'
    ) {
        //test
        return true;
    }
    return false;
}

/**
 *
 * @param viewer
 * @returns {boolean}
 */
export function isAssembly(viewer) {
    if (!viewer) {
        return false;
    }

    // if the top ps node has children assume it's an assembl
    const info = viewer.getProductStructureInfo(viewer.psId, [], 1);
    if (info.children?.length > 0) {
        const topModel = info.children[info.children.length - 1];
        const isPart = (psId) => viewer.getObjectTypeByPsId(psId) === 'Part';
        return topModel?.childrenIds?.some(isPart);
    }
    return false;
}

/**
 * This function determines if we should perform selection scope checking
 *
 * @param {object} viewer - Instance of plmvisweb viewer
 * @param {SelectionObject[]} candidateObjects - Array of selection candidate objects
 * @param {SELECTION_SCOPE} [selectionScope] - Selection scope
 * @returns {boolean}
 */
function checkSelectionScope(viewer, candidateObjects, selectionScope) {
    const partContext = getPartContextCache();
    if (!partContext) {
        return false;
    }

    let doChecking = true;
    if (doChecking && selectionScope === SELECTION_SCOPE.WithinWorkPartAndComponents && !partContext.workComponentAtlasId) {
        // selection withing work part and components but work part is undefined, skip checking
        doChecking = false;
    }
    if (doChecking && (!selectionScope || selectionScope === SELECTION_SCOPE.EntireAssembly)) {
        // selection scope is undefined or for the entire assembly, skip checking
        doChecking = false;
    }
    if (doChecking && !isAssembly(viewer)) {
        // don't check selection scope if part is a piece part
        doChecking = false;
    }
    if (doChecking && selectionScope === SELECTION_SCOPE.WithinWorkPartAndComponents) {
        // if workpart === rootpart, skip checking
        if (JSON.stringify(partContext.displayPartAtlasId) === JSON.stringify(partContext.workPartAtlasId)) {
            doChecking = false;
        }
    }
    return doChecking;
}

/**
 * Given the candidate objects and the selection scope, this function filters out the objects that are not
 * in the selection scope and returns the objects in belonging to the selection scope.
 *
 * @param {object} viewer - instance of plmvisweb viewer
 * @param {SelectionObject[]} candidateObjects - the candidate objects to check the selection scope
 * @param {SELECTION_SCOPE} [selectionScope] - the selection scope
 * @returns {object[]} - the candidate objects that are in the selection scope
 */
function filterOutOfScopeObjects(viewer, candidateObjects, selectionScope) {
    if (!checkSelectionScope(viewer, candidateObjects, selectionScope)) {
        // return candidates as they are if skip checking
        return candidateObjects;
    }
    const partContext = getPartContextCache();
    if (!partContext) {
        // Skip check if part context is undefined
        return candidateObjects;
    }
    let inScopeObjSet = new Set();
    if (candidateObjects) {
        // workComponentAtlasId is not set means that we are at the top of the assembly.
        // In this case, display part and work part are the same. Both "Entire Assembly" and "Within Work Part and Components"
        // need to go through the entire assembly. In this case we don't need to do any selection scope checking.
        // checkSelectionScope will skip the checking. We only need to set childCompSet as undefined.
        for (const obj of candidateObjects) {
            let isInScope = false;
            if (ignoreObjectFromSelectionScope(obj)) {
                isInScope = true;
            } else {
                // isSameContext compares the component of each id, true if the same.  So if this
                // object came from workComponentAtlasId, this should return true.
                /** @type {Array} */
                let objAtlasId = null;
                if (Array.isArray(obj.id)) {
                    objAtlasId = obj.id;
                } else if (typeof obj.id === 'string' && obj.psIds && obj.psIds.length > 0) {
                    // This is any of the snap points. check the parent
                    objAtlasId = ConvertIds.fromPsId(viewer, obj.psIds[0]);
                }
                // if partContext.workComponentAtlasId is not set, we're at the top of the assembly. In this
                // case we should only allow the objects in the top of the assembly to pass. PLMVisWeb returns
                // the objects in the top assembly (root part) with the id in the piece part. Therefore, we
                // should use the piece part (workPart which is the same as displayPart in this case) to check
                const partAtlasId =
                    JSON.stringify(partContext.workPartAtlasId) !== JSON.stringify(partContext.displayPartAtlasId)
                        ? partContext.workComponentAtlasId
                        : partContext.workPartAtlasId;
                isInScope = ConvertIds.isSameContext(objAtlasId, partAtlasId);
                if (!isInScope) {
                    // Check two cases:
                    // a) if object is an component and the component part is directly in Work Part.
                    //    Now we skip part checking only when selection is from Assembly Navigator
                    // b) if object is geometry check if the geometry is in any of the child components
                    const isWorkOrComponent = obj?.type === 'Part';
                    if (
                        selectionScope === SELECTION_SCOPE.WithinWorkPartAndComponents ||
                        (selectionScope === SELECTION_SCOPE.WithinWorkPartOnly && isWorkOrComponent)
                    ) {
                        if (!partContext.workComponentAtlasId) {
                            // top level - all parts in scope
                            // For SELECTION_SCOPE.WithinWorkPartAndComponents, geometry and part, etc. outside the root part can come here.
                            // For SELECTION_SCOPE.WithinWorkPartOnly, only part will come here.
                            isInScope = true;
                        } else {
                            const isSameContext = (compAtlasId) => ConvertIds.isSameContext(objAtlasId, compAtlasId);
                            const childCompSet = getChildComponentSet(partContext.workComponentAtlasId);
                            isInScope = childCompSet && Array.from(childCompSet || []).some(isSameContext);
                        }
                    }
                }
            }
            if (isInScope) {
                inScopeObjSet.add(obj);
            }
        }
    }
    return Array.from(inScopeObjSet);
}

/**
 * When the ids of the selection objects are in Atlas Id form, this function filters out the objects out of the
 * selection scope
 *
 * @param {object} viewer - instance of plmvisweb viewer object
 * @param {SelectionObject[]} addObjects - array of selectiion objects to add.
 * @param {SelectionObject[]} removeObjects - array of selection objects to remove
 * @param {SELECTION_SCOPE} [selectionScope] - selectionScope defined in SELECTION_SCOPE
 * @returns {object} - Object containing arrays (addObjects, removeObjects) of the objects excluding the out of selection scope ones
 */
export function processSelectionScope(viewer, addObjects, removeObjects, selectionScope) {
    let adjustedAddObjects = filterOutOfScopeObjects(viewer, addObjects, selectionScope);
    let adjustedRemoveObjects = filterOutOfScopeObjects(viewer, removeObjects, selectionScope);
    return { addObjects: adjustedAddObjects, removeObjects: adjustedRemoveObjects };
}

/**
 * Converts the atlasId into psId using the current model Id
 *
 * @param {object} viewer -
 * @param {Array} atlasId -
 * @returns {string} the psId corresponding to the Atlas Id
 */
export function toPsId(viewer, atlasId) {
    const info = viewer.getProductStructureInfo(viewer.psId, [], 1);
    const modelId = info.children[info.children.length - 1].psId.split(':')[0];
    return ConvertIds.toPsId(modelId, atlasId);
}

/**
 *
 * @param {object} viewer
 * @param {Array} atlasId
 * @returns {boolean}
 */
export function hasChildComponent(viewer, atlasId) {
    let objType = viewer.getObjectTypeByPsId(toPsId(viewer, atlasId));
    if (objType === 'Part' || objType === 'Assembly' || objType === '') {
        // Potential to be an asm (asm with bodies will return the type as Part. Check the children count to be sure)
        let children = getChildComponentSet(atlasId);
        if (children?.size) {
            return true;
        }
    }

    return false;
}

/**
 * Transform the point in display space into the object prototype space
 *
 * @param {object} viewer - an instance of PLMVisWeb viewer object
 * @param {object} plmvisweb - an instance of PLMVisWeb object
 * @param {string} occPsId - psId of an object occurrence or object in piece part
 * @param {object} point - point object in {x: xValue, y: yValue, z: zValue} in the display space
 * @returns {object} - point location in the object prototype space in the form of {x: xValue, y: yValue, z: zValue}
 */
export function getPointInProtoSpace(viewer, plmvisweb, occPsId, point) {
    const worldMatrixArray = viewer.getWorldMatrixByPsId(occPsId);
    let worldMatrixInv = new plmvisweb.THREE.Matrix4();
    worldMatrixInv.fromArray(worldMatrixArray);
    worldMatrixInv.getInverse(worldMatrixInv);
    let refPoint = new plmvisweb.THREE.Vector3(point.x, point.y, point.z);
    refPoint.applyMatrix4(worldMatrixInv);
    // Use the following two lines to replace the above lines when PLMVisWeb fixes the bug in viewer.convertWorldToLocalByPsId
    // const pointInArray = [point.x, point.y, point.z];
    // let refPoint = viewer.convertWorldToLocalByPsId(occPsId, pointInArray);
    return refPoint;
}

/**
 * Sets if the mouse is on canvas
 *
 * @param {boolean} onCanvas - true the mouse is on Canvas; false otherwise such as on a dialog, etc.
 */
let _mouseOnCanvas = undefined;
/**
 *
 * @param {boolean} onCanvas
 */
export function setMouseOnCanvas(onCanvas) {
    _mouseOnCanvas = onCanvas;
}

/**
 * Gets mouse on canvas status
 *
 * @returns {boolean}
 */
function getMouseOnCanvas() {
    return _mouseOnCanvas;
}

let _priorityDistance = false;
/**
 * Sets priority distance - allows selection to return objects with distance closest to the cursor first
 *
 * @param {boolean} priorityDistance - true or false
 */
export function setPriorityDistance(priorityDistance) {
    _priorityDistance = priorityDistance;
}

/**
 * Gets priority distance
 *
 * @returns {boolean} true to return selection objects with distance closest to the cursor first
 */
export function getPriorityDistance() {
    return _priorityDistance;
}

export default {
    isCurveFromPsId,
    isPointFromPsId,
    isDimension,
    getObjectTypeByPsId,
    dist2,
    dist3,
    isSame2DLocation,
    isSame3DLocation,
    generateSelObj,
    mouseMoveCallback,
    sortPsIds,
    handleEdgeOnTopInContext,
    setDimensionSelection,
    displaySelectedInContext,
    createDimensionColorObject,
    setMaterialByPsIds,
    getSelectedObjectsInactiveContexts,
    setPrimarySelectionColor,
    getPointCache,
    getCachedPoints,
    resetPointCache,
    SELECTION_COLORS,
    SELECTION_REASONS,
    DISPLAY_DELAY,
    setPointCacheRegistered,
    getPointCacheRegistered,
    registerPointCache,
    onWorkPartChanged,
    setAllowSelectionOfHiddenWireframe,
    getAllowSelectionOfHiddenWireframe,
    SELECTION_SCOPE,
    SnapOptions,
    setDefaultSnapOption,
    getDefaultSnapOption,
    setDefaultSelectionScope,
    getDefaultSelectionScope,
    registerGetPartContextCache_fn,
    processSelectionScope,
    registerGetChildComponents_fn,
    getPartContextCache,
    getChildComponentSet,
    toPsId,
    hasChildComponent,
    getPointInProtoSpace,
    getInheritOriginalCurveStyles,
    setInheritOriginalCurveStyles,
    setCurveHighlightStyles,
    setCurveSelectionStyles,
    isAssembly,
    registerGetParentComponents_fn,
    getParentComponents,
    setMouseOnCanvas,
    setMouseHoverDisplayDelay,
    getMouseHoverDisplayDelay,
    setPriorityDistance,
    getPriorityDistance
};
